import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiEyeOff, FiMail, FiMessageCircle, FiCheckCircle, FiCopy, FiXCircle, FiSearch, FiX, FiChevronLeft, FiChevronRight, FiExternalLink, FiChevronDown } from 'react-icons/fi';
import api, { SeriesAPI, OrdersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import HeaderDateFilters from './Tasks/components/Filters/HeaderDateFilters';
import CustomerDetail from './CustomerDetail';
import PaymentModal from '../components/PaymentModal';
import { colors, matte, tableStyles, billingTableCSS } from '../styles/billingTableStyles';

const getCurrentFiscalYearRange = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const fyStartMonth = 3; // April (0-indexed)
  const fyStartYear = now.getMonth() >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
  const start = new Date(fyStartYear, fyStartMonth, 1, 0, 0, 0, 0);
  const end = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999);
  return { start, end };
};

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.stone50} 0%, ${colors.stone100} 100%)`,
    padding: '24px 32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.stone800,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 13,
    color: colors.stone500,
    margin: '2px 0 0',
  },
  headerActions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    border: `1px solid ${colors.stone200}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)',
    marginBottom: 16,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.stone100}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 12,
  },
  statCard: {
    background: colors.stone800,
    borderRadius: 12,
    padding: 14,
    border: `1px solid ${colors.stone700}`,
  },
  statTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.stone300,
    marginBottom: 10,
  },
  segBar: {
    display: 'flex',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    background: colors.stone700,
  },
  legendGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
    marginTop: 10,
  },
  legendItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 8px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    fontSize: 11,
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  legendItemActive: {
    border: '1px solid rgba(255,255,255,0.5)',
    background: 'rgba(255,255,255,0.1)',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    maxWidth: 280,
    minWidth: 180,
  },
  searchIcon: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.stone400,
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '7px 10px 7px 32px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone800,
    outline: 'none',
  },
  select: {
    padding: '7px 26px 7px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone700,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  },
  ...tableStyles,
  btnIcon: { ...tableStyles.btnIcon, width: 22, height: 22 },
  btnPrimary: {
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    background: `linear-gradient(135deg, ${colors.accent500} 0%, ${colors.accent600} 100%)`,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 2px 6px rgba(6, 182, 212, 0.25)',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  pageBtn: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 6,
    color: colors.stone600,
    cursor: 'pointer',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    padding: '10px 16px',
    background: colors.stone800,
    color: '#fff',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 500,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    animation: 'slideUp 0.3s ease',
  },
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 520,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.stone200}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalBody: {
    padding: '16px 20px',
  },
  modalFooter: {
    padding: '12px 20px',
    borderTop: `1px solid ${colors.stone100}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  drawer: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  drawerContent: {
    width: '60%',
    maxWidth: 900,
    height: '100%',
    background: '#fff',
    boxShadow: '-8px 0 32px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: 12,
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: colors.stone500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: colors.stone50,
    color: colors.stone800,
    outline: 'none',
  },
};

const getStatusDotColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'CONVERTED') return matte.green;
  if (s === 'ACCEPTED') return matte.teal;
  if (s === 'SENT') return matte.blue;
  if (s === 'REJECTED' || s === 'CANCELLED') return matte.red;
  if (s === 'DRAFT' || s === 'PENDING') return matte.blue;
  return matte.grey;
};

const getSourceBadge = (source) => {
  const s = String(source || '').toUpperCase();
  if (s === 'ORDER') return { color: colors.stone800, label: 'Order' };
  if (s === 'RECURRING') return { color: colors.stone800, label: 'Recurring' };
  return { color: colors.stone700, label: 'Direct' };
};

export default function Quotes() {
  const navigate = useNavigate();
  const { company, user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const { start, end } = getCurrentFiscalYearRange();
    const toYMD = (iso) => new Date(iso).toISOString().slice(0,10);
    const params = new URLSearchParams(window.location.search);
    return {
      status: params.get('status') || '',
      statusGroup: '',
      source: params.get('source') || '',
      advancePaidPending: '',
      customerId: '',
      fromDate: params.get('fromDate') || toYMD(start.toISOString()),
      toDate: params.get('toDate') || toYMD(end.toISOString()),
      number: ''
    };
  });
  const [stats, setStats] = useState({
    totalCount: 0, convertedCount: 0, rejectedCount: 0, totalAmount: 0, convertedAmount: 0,
    bySource: { DIRECT: 0, ORDER: 0, RECURRING: 0 }, pendingCount: 0, advancePaidPendingCount: 0
  });
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [toast, setToast] = useState('');
  const [waModal, setWaModal] = useState({ open: false, id: null, phone: '', message: '' });
  const [convertModal, setConvertModal] = useState({ open: false, id: null, dueAt: '' });
  const [payModal, setPayModal] = useState({ open: false, id: null, dueAt: '' });
  const [seriesState, setSeriesState] = useState({ loading: false, saving: false, id: null, prefix: '', prefixPre: '', suffix: '', currentSeq: 0, error: '', fy: '', fySep: '/' });
  const [formatModal, setFormatModal] = useState(false);
  const [formatOptions, setFormatOptions] = useState([]);
  const [selectedFormatId, setSelectedFormatId] = useState(null);
  const [formatActivatingId, setFormatActivatingId] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pFromDate = params.get('fromDate');
    const pToDate = params.get('toDate');
    if (pFromDate && pToDate) return { startDate: pFromDate, endDate: pToDate, label: 'Custom' };
    const { start, end } = getCurrentFiscalYearRange();
    return { startDate: start.toISOString(), endDate: end.toISOString(), label: 'Current FY' };
  });
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', label: '', kind: 'Quote', loading: false });
  const [payInfo, setPayInfo] = useState({ open: false, inv: null });
  const [customerDrawer, setCustomerDrawer] = useState({ open: false, id: null, customer: null });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkConvert, setBulkConvert] = useState({ open: false, dueDate: '', paymentMethod: '', paymentReference: '', paymentDate: '', proofUrl: '', uploading: false });
  const [advanceModal, setAdvanceModal] = useState({ open: false, id: null, amount: '', percentage: '', paymentMethod: '', paymentReference: '', paymentDate: '', notes: '', paidNow: true, loading: false, summary: null });
  const [proformaPayModal, setProformaPayModal] = useState({ open: false, proformaId: null });
  const [showStats, setShowStats] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem('quotes.showStats') ?? 'true'); } catch { return true; }
  });

  const params = useMemo(() => ({ page, pageSize, ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)) }), [page, pageSize, filters]);

  const fetchCustomers = async () => {
    if (!company?.id) return;
    setCustomersLoading(true);
    try {
      const { data } = await api.get(`/api/companies/${company.id}/customers`);
      const list = Array.isArray(data) ? data : (Array.isArray(data?.customers) ? data.customers : []);
      setCustomers(list.map(c => ({ id: c.id, name: c.name, phone: c.phone || '' })));
    } catch (e) { console.error(e); }
    finally { setCustomersLoading(false); }
  };

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/quotes', { params });
      setQuotes(data.data || []);
      setTotal(data.total || 0);
      if (data.stats) setStats(data.stats);
    } catch (e) { console.error(e); setToast('Failed to load quotes'); }
    finally { setLoading(false); }
  };

  const localCounts = useMemo(() => {
    const bySource = { DIRECT: 0, ORDER: 0, RECURRING: 0 };
    let pendingCount = 0, advancePaidPendingCount = 0, rejectedCount = 0;
    for (const q of quotes) {
      bySource[q.source || 'DIRECT'] = (bySource[q.source || 'DIRECT'] || 0) + 1;
      const isPending = !['CONVERTED', 'REJECTED', 'CANCELLED'].includes(String(q.status || '').toUpperCase());
      if (isPending) pendingCount++;
      if (String(q.status || '').toUpperCase() === 'REJECTED') rejectedCount++;
      const hasPaidAdvance = (q.proformas || []).some(p => String(p?.paymentStatus || '').toUpperCase() === 'PAID');
      if (isPending && hasPaidAdvance) advancePaidPendingCount++;
    }
    return { bySource, pendingCount, advancePaidPendingCount, rejectedCount };
  }, [quotes]);

  const applyStatFilters = (patch) => {
    setFilters(prev => ({ ...prev, status: '', statusGroup: '', source: '', advancePaidPending: '', ...patch }));
    setPage(1);
  };

  const isStatActive = (key) => {
    const f = filters;
    if (key === 'ALL') return !f.status && !f.statusGroup && !f.source && !f.advancePaidPending;
    if (key === 'CONVERTED') return f.status?.toUpperCase() === 'CONVERTED';
    if (key === 'REJECTED') return f.status?.toUpperCase() === 'REJECTED';
    if (key === 'PENDING') return f.statusGroup?.toUpperCase() === 'PENDING' && !f.advancePaidPending;
    if (key === 'ADVANCE_PAID_PENDING') return f.advancePaidPending === '1';
    if (key === 'SRC_DIRECT') return f.source?.toUpperCase() === 'DIRECT';
    if (key === 'SRC_ORDER') return f.source?.toUpperCase() === 'ORDER';
    if (key === 'SRC_RECURRING') return f.source?.toUpperCase() === 'RECURRING';
    return false;
  };

  useEffect(() => { fetchQuotes(); }, [page, pageSize, filters]);
  useEffect(() => { if (company?.id) { fetchCustomers(); } }, [company?.id]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(''), 3000); return () => clearTimeout(t); }, [toast]);
  useEffect(() => { try { window.localStorage.setItem('quotes.showStats', JSON.stringify(showStats)); } catch {} }, [showStats]);

  // Series state initialization
  useEffect(() => {
    if (!company?.id) return;
    (async () => {
      setSeriesState(s => ({ ...s, loading: true }));
      try {
        const cur = await SeriesAPI.current(company.id, 'QUOTE');
        const fyFromBounds = (() => {
            if (cur.fyStart && cur.fyEnd) {
              const y1 = String(new Date(cur.fyStart).getUTCFullYear()).slice(-2);
              const y2 = String(new Date(cur.fyEnd).getUTCFullYear()).slice(-2);
              return `${y1}-${y2}`;
            }
            return '';
        })();
        const fullPrefix = cur.prefix || '';
        const fyIdx = fyFromBounds ? fullPrefix.indexOf(fyFromBounds) : -1;
        let prefixPre = fullPrefix, fySep = '/';
        if (fyIdx >= 0) {
          prefixPre = fullPrefix.slice(0, fyIdx);
          const charBefore = fullPrefix[fyIdx - 1];
          const charAfter = fullPrefix[fyIdx + fyFromBounds.length];
          if (charBefore && '/-_ .'.includes(charBefore)) { fySep = charBefore; if (prefixPre.endsWith(fySep)) prefixPre = prefixPre.slice(0, -1); }
        }
        setSeriesState({ loading: false, saving: false, id: cur.id, prefix: fullPrefix, prefixPre, suffix: cur.suffix || '', currentSeq: cur.currentSeq || 0, error: '', fy: fyFromBounds, fySep });
      } catch {
        const computeFY = () => { const dt = new Date(), yr = dt.getUTCFullYear(), mon = dt.getUTCMonth() + 1; const s = mon >= 4 ? yr : yr - 1, e = mon >= 4 ? yr + 1 : yr; return `${String(s).slice(-2)}-${String(e).slice(-2)}`; };
        setSeriesState({ loading: false, saving: false, id: null, prefix: '', prefixPre: '', suffix: '', currentSeq: 0, error: '', fy: computeFY(), fySep: '/' });
      }
    })();
  }, [company?.id]);

  const formatINR = (val) => {
    try { return '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Number(val || 0)); } catch { return val; }
  };

  const pendingAdvanceAmount = useMemo(() => {
      const list = advanceModal?.summary?.proformas || [];
    return list.reduce((acc, p) => {
      if (String(p?.paymentStatus || '').toUpperCase() === 'PAID') return acc;
      return acc + Math.max(0, Number(p?.total || 0) - Number(p?.amountPaid || 0));
      }, 0);
  }, [advanceModal?.summary]);

  const handleDateChange = (val) => {
    if (val === 'all') { setDateRange({ startDate: null, endDate: null }); setFilters(f => ({ ...f, fromDate: '', toDate: '' })); setPage(1); return; }
    const toYMD = (iso) => new Date(iso).toISOString().slice(0,10);
    setDateRange(val);
    setFilters(f => ({ ...f, fromDate: toYMD(val.startDate), toDate: toYMD(val.endDate) }));
    setPage(1);
  };

  const handleReject = async (id) => {
    const key = `reject:${id}`;
    setActionLoading(s => ({ ...s, [key]: true }));
    try { await api.post(`/api/quotes/${id}/status`, { status: 'REJECTED' }); setToast('Quote rejected'); fetchQuotes(); }
    catch (e) { setToast('Failed to reject'); }
    finally { setActionLoading(s => { const n = { ...s }; delete n[key]; return n; }); }
  };

  const handleSendEmail = async (id) => {
    const key = `email:${id}`;
    setActionLoading(s => ({ ...s, [key]: true }));
    try { await api.post(`/api/quotes/${id}/send`); setToast('Email sent'); fetchQuotes(); }
    catch { setToast('Failed to send email'); }
    finally { setActionLoading(s => { const n = { ...s }; delete n[key]; return n; }); }
  };

  const openPdfPreview = async (q) => {
    try {
      setPdfPreview({ open: true, url: '', label: q.number || '', kind: 'Quote', loading: true });
      const { data } = await api.get(`/api/quotes/${q.id}/pdf-url`);
      if (!data?.url) throw new Error('No URL');
      setPdfPreview(p => ({ ...p, url: data.url, loading: false }));
    } catch { setToast('Failed to load PDF'); setPdfPreview({ open: false, url: '', label: '', kind: 'Quote', loading: false }); }
  };

  const openInvoicePdfPreview = async (q) => {
    try {
      setPdfPreview({ open: true, url: '', label: q.number || '', kind: 'Invoice', loading: true });
      const { data } = await api.get(`/api/quotes/${q.id}/invoice-pdf-url`);
      if (!data?.url) throw new Error('No URL');
      setPdfPreview(p => ({ ...p, url: data.url, label: data.invoiceNumber || data.number || q.number, loading: false }));
    } catch { setToast('Failed to load Invoice PDF'); setPdfPreview({ open: false, url: '', label: '', kind: 'Invoice', loading: false }); }
  };

  const closePdfPreview = () => setPdfPreview({ open: false, url: '', label: '', kind: 'Quote', loading: false });

  const openCustomerDetail = (q) => {
    const id = q?.customerSnapshot?.id || q?.customerId;
      if (!id) return;
    setCustomerDrawer({ open: true, id, customer: q.customerSnapshot ? { id, ...q.customerSnapshot } : { id } });
  };

  const toggleSelected = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const openBulkConvert = () => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    const pad = n => String(n).padStart(2, '0');
    setBulkConvert({ open: true, dueDate: `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`, paymentMethod: '', paymentReference: '', paymentDate: new Date().toISOString().slice(0,10), proofUrl: '', uploading: false });
  };

  const confirmBulkConvert = async () => {
    if (!selectedIds.length) return;
    try {
      const selectedQuoteNumbers = selectedIds
        .map(id => quotes.find(q => String(q.id) === String(id))?.number)
        .filter(Boolean);
      const payload = {
        quoteNumbers: selectedQuoteNumbers,
        // keep ids for backwards compatibility / partial list safety
        quoteIds: selectedIds,
        date: new Date().toISOString().slice(0,10),
        dueDate: bulkConvert.dueDate
      };
      const { data } = await api.post('/api/invoices/convert-from-quotes', payload);
      if (data?.id && bulkConvert.paymentMethod) {
        await api.post(`/api/invoices/${data.id}/mark-paid`, { paymentMethod: bulkConvert.paymentMethod, paymentReference: bulkConvert.paymentReference, paymentProofUrl: bulkConvert.proofUrl, paymentDate: bulkConvert.paymentDate });
      }
      setToast('Created consolidated invoice');
      setSelectedIds([]);
      setBulkConvert({ open: false, dueDate: '', paymentMethod: '', paymentReference: '', paymentDate: '', proofUrl: '', uploading: false });
      fetchQuotes();
    } catch (e) { setToast(e?.response?.data?.error || 'Failed to consolidate'); }
  };

  const openPayModal = (q) => {
    // For "Accept & Mark Paid", invoice due date is always current (today)
    const today = new Date().toISOString().slice(0, 10);
    setPayModal({ open: true, id: q.id, dueAt: today, paymentMethod: '', paymentReference: '', paymentDate: today, proofUrl: '', uploading: false });
  };

  const handleSubmitAcceptAndPay = async (paymentData) => {
    const { id, dueAt } = payModal;
    if (!id) return;
    const key = `accept:${id}`;
    setActionLoading(s => ({ ...s, [key]: true }));
    try {
      let linkedOrder = null;
      if (company?.id) {
        const { data: ordRes } = await api.get('/api/orders', { params: { companyId: company.id, pageSize: 200 } });
        const ords = Array.isArray(ordRes?.data) ? ordRes.data : [];
        linkedOrder = ords.find(o => String(o.quoteId) === String(id));
      }
      if (linkedOrder?.id) {
        await OrdersAPI.markPaid(linkedOrder.id, paymentData);
      } else {
        const { data } = await api.post(`/api/quotes/${id}/convert-to-invoice`, { date: new Date().toISOString().slice(0,10), dueDate: dueAt, createdFrom: 'CONVERTED_QUOTE' });
        let invoiceId = data?.id;
        if (!invoiceId) { const resp = await api.get(`/api/quotes/${id}/invoice-pdf-url`); invoiceId = resp?.data?.invoiceId; }
        if (!invoiceId) throw new Error('Invoice not created');
        await api.post(`/api/invoices/${invoiceId}/mark-paid`, paymentData);
      }
      setToast('Invoice created and marked as paid');
      setPayModal({ open: false, id: null, dueAt: '' });
      fetchQuotes();
    } catch (e) { setToast(e?.response?.data?.error || 'Failed'); }
    finally { setActionLoading(s => { const n = { ...s }; delete n[key]; return n; }); }
  };

  const openAdvanceModal = async (q) => {
    setAdvanceModal({ open: true, id: q.id, amount: '', percentage: '', paymentMethod: '', paymentReference: '', paymentDate: new Date().toISOString().slice(0,10), notes: '', paidNow: true, loading: true, summary: null });
    try {
      const { data } = await api.get(`/api/quotes/${q.id}/advances`);
      setAdvanceModal(m => ({ ...m, loading: false, summary: data }));
    } catch { setAdvanceModal(m => ({ ...m, loading: false })); }
  };

  const submitAdvance = async () => {
    if (!advanceModal.id) return;
    setAdvanceModal(m => ({ ...m, loading: true }));
    try {
      await api.post(`/api/quotes/${advanceModal.id}/record-advance`, {
        amount: advanceModal.amount ? Number(advanceModal.amount) : undefined,
        percentage: advanceModal.percentage ? Number(advanceModal.percentage) : undefined,
        paymentMethod: advanceModal.paymentMethod || undefined,
        paymentReference: advanceModal.paymentReference || undefined,
        paymentDate: advanceModal.paymentDate || undefined,
        notes: advanceModal.notes || undefined,
        paid: !!advanceModal.paidNow
      });
      setToast('Advance recorded');
      setAdvanceModal({ open: false, id: null, amount: '', percentage: '', paymentMethod: '', paymentReference: '', paymentDate: '', notes: '', paidNow: true, loading: false, summary: null });
      fetchQuotes();
    } catch (e) { setToast(e?.response?.data?.error || 'Failed'); setAdvanceModal(m => ({ ...m, loading: false })); }
  };

  const handleProofUpload = async (e, setState) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setState(m => ({ ...m, uploading: true }));
    try {
      const resp = await api.get('/api/upload/presigned-url', { params: { fileType: file.type } });
      await fetch(resp.data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      setState(m => ({ ...m, proofUrl: resp.data.fileUrl || resp.data.uploadUrl.split('?')[0], uploading: false }));
      setToast('Proof uploaded');
    } catch { setState(m => ({ ...m, uploading: false })); setToast('Failed to upload'); }
  };

  const submitProformaPayment = async (paymentData) => {
    const { proformaId } = proformaPayModal;
    if (!proformaId) return;
    const key = `proformaPay:${proformaId}`;
    setActionLoading(s => ({ ...s, [key]: true }));
    try {
      await api.post(`/api/proformas/${proformaId}/payment`, { ...paymentData, paymentStatus: 'PAID' });
      setToast('Payment recorded');
      setProformaPayModal({ open: false, proformaId: null });
      fetchQuotes();
    } catch (e) { setToast(e?.response?.data?.error || 'Failed'); }
    finally { setActionLoading(s => { const n = { ...s }; delete n[key]; return n; }); }
  };

  const openWhatsAppModal = async (q) => {
    const phone = (q.customerSnapshot?.phone || user?.phone || '').toString();
    let message = `Hello ${q.customerSnapshot?.name || ''}, here is Quote ${q.number} dated ${q.date} for ${formatINR(q.total)}.`;
    try { const { data } = await api.get(`/api/quotes/${q.id}/pdf-url`); if (data?.url) message += `\nPDF: ${data.url}`; } catch {}
    message += `\nThank you!`;
    setWaModal({ open: true, id: q.id, phone, message });
  };

  const handleSendWhatsApp = () => {
    const { phone, message } = waModal;
    const digits = (phone || '').replace(/\D/g, '');
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(message)}`, '_blank');
    setWaModal({ open: false, id: null, phone: '', message: '' });
  };

  const saveSeriesFormat = async () => {
    if (!company?.id) return;
    setSeriesState(s => ({ ...s, saving: true }));
    try {
      const composedPrefix = `${seriesState.prefixPre || ''}${seriesState.fySep || '/'}${seriesState.fy || ''}${seriesState.fySep || '/'}`;
      const targetDate = seriesState.fy ? `20${seriesState.fy.slice(0,2)}-04-01` : new Date().toISOString().slice(0,10);
      await SeriesAPI.setFormat({ companyId: company.id, documentType: 'QUOTE', prefix: composedPrefix, suffix: seriesState.suffix || '', date: targetDate });
      const [cur, list] = await Promise.all([SeriesAPI.current(company.id, 'QUOTE'), SeriesAPI.list({ companyId: company.id, documentType: 'QUOTE' })]);
      setFormatOptions(Array.isArray(list) ? list : []);
      setSeriesState(s => ({ ...s, saving: false, id: cur.id, prefix: cur.prefix || composedPrefix, currentSeq: cur.currentSeq || s.currentSeq }));
      setToast('Format saved');
      setFormatModal(false);
    } catch { setSeriesState(s => ({ ...s, saving: false })); setToast('Failed to save'); }
  };

  useEffect(() => {
    if (!company?.id || !formatModal) return;
    SeriesAPI.list({ companyId: company.id, documentType: 'QUOTE' }).then(list => setFormatOptions(Array.isArray(list) ? list : [])).catch(() => {});
  }, [company?.id, formatModal]);

  const applyExistingFormat = async (row) => {
    setFormatActivatingId(row.id);
    try {
      const targetDate = row.fyStart ? String(row.fyStart).slice(0,10) : new Date().toISOString().slice(0,10);
        await SeriesAPI.setFormat({ companyId: company.id, documentType: 'QUOTE', prefix: row.prefix, suffix: row.suffix || '', date: targetDate });
      const [cur, list] = await Promise.all([SeriesAPI.current(company.id, 'QUOTE'), SeriesAPI.list({ companyId: company.id, documentType: 'QUOTE' })]);
        setFormatOptions(Array.isArray(list) ? list : []);
        setSeriesState(s => ({ ...s, id: cur.id, currentSeq: cur.currentSeq || s.currentSeq }));
      setSelectedFormatId(row.id);
    } catch {}
    finally { setFormatActivatingId(null); }
  };

  // Stats values
  const pendingVal = stats.pendingCount ?? localCounts.pendingCount;
  const advPendingVal = stats.advancePaidPendingCount ?? localCounts.advancePaidPendingCount;
              const convertedVal = stats.convertedCount || 0;
  const rejectedVal = stats.rejectedCount ?? localCounts.rejectedCount;
  const directVal = stats.bySource?.DIRECT ?? localCounts.bySource.DIRECT;
  const orderVal = stats.bySource?.ORDER ?? localCounts.bySource.ORDER;
  const recurringVal = stats.bySource?.RECURRING ?? localCounts.bySource.RECURRING;
  const directSrcAmt = stats.bySourceAmount?.DIRECT;
  const orderSrcAmt = stats.bySourceAmount?.ORDER;
  const recurringSrcAmt = stats.bySourceAmount?.RECURRING;
                const totalAmt = Number(stats.totalAmount || 0);
                const convAmt = Number(stats.convertedAmount || 0);
                const rejAmt = stats.rejectedAmount != null ? Number(stats.rejectedAmount || 0) : 0;
                const pendingAmt = stats.pendingAmount != null ? Number(stats.pendingAmount || 0) : Math.max(0, +(totalAmt - convAmt - rejAmt).toFixed(2));
                const advPendingAmt = stats.advancePaidPendingAmount != null ? Number(stats.advancePaidPendingAmount || 0) : 0;
                const pendingNoAdvAmt = Math.max(0, +(pendingAmt - advPendingAmt).toFixed(2));

  const SegmentBar = ({ segments }) => {
    const sum = segments.reduce((a, s) => a + Number(s.value || 0), 0) || 1;
                  return (
      <div style={styles.segBar}>
                      {segments.map((s, i) => (
          <button key={i} onClick={s.onClick} title={s.title} style={{ width: `${(Number(s.value || 0) / sum) * 100}%`, background: s.color, border: 'none', padding: 0, cursor: s.onClick ? 'pointer' : 'default' }} />
                      ))}
                    </div>
                  );
                };

  const LegendItem = ({ label, value, amount, color, active, onClick }) => (
    <button style={{ ...styles.legendItem, ...(active ? styles.legendItemActive : {}) }} onClick={onClick}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
        {label}
                        </span>
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
        <span style={{ fontWeight: 600 }}>{value}</span>
        {amount !== undefined && (
          <span style={{ /* fontSize: 10,  */color: colors.stone300 }}>{formatINR(amount)}</span>
        )}
      </span>
                      </button>
  );

  const renderPagination = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: `1px solid ${colors.stone100}` }}>
      <span style={{ fontSize: 11, color: colors.stone500 }}>
        Showing <strong style={{ color: colors.stone700 }}>{Math.min((page - 1) * pageSize + 1, total)}-{Math.min(page * pageSize, total)}</strong> of <strong style={{ color: colors.stone700 }}>{total}</strong>
      </span>
      <div style={styles.pagination}>
        <button style={{ ...styles.pageBtn, opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><FiChevronLeft size={14} /></button>
        <span style={{ padding: '0 10px', fontSize: 11, color: colors.stone600 }}>Page {page}</span>
        <button style={{ ...styles.pageBtn, opacity: page * pageSize >= total ? 0.5 : 1 }} disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}><FiChevronRight size={14} /></button>
        <select style={{ ...styles.select, marginLeft: 6 }} value={pageSize} onChange={e => { setPageSize(+e.target.value); setPage(1); }}>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
      </div>
                  </div>
                );

                return (
                  <div className="page-content">
      <style>{billingTableCSS()}</style>

      {/* Toast */}
      {!!toast && (
        <div style={styles.toast}>
          <span>{toast}</span>
          <button onClick={() => setToast('')} style={{ ...styles.btnIcon, color: '#fff' }}><FiX size={14} /></button>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div>
            <h1 style={styles.title}>Quotes</h1>
            <p style={styles.subtitle}>{total} quotes</p>
                        </div>
                        </div>
        <div style={styles.headerActions}>
          <HeaderDateFilters value={dateRange} onChange={handleDateChange} />
          <button style={styles.btnSecondary} disabled={!selectedIds.length} onClick={openBulkConvert}>Create Invoice from selected</button>
          <button style={styles.btnSecondary} onClick={() => setFormatModal(true)}>Number Format</button>
          <Link
            to="/quote/new"
            style={{ ...styles.btnSecondary, textDecoration: 'none' }}
          >
            <FiPlus size={14} /> New Quote
          </Link>
                        </div>
                      </div>

      {/* Stats */}
      {showStats && (
        <div style={{ ...styles.card, background: colors.stone800, border: `1px solid ${colors.stone700}` }}>
          <div style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Overview</span>
              <button style={{ ...styles.btnIcon, color: colors.stone400 }} onClick={() => setShowStats(false)}><FiEyeOff size={14} /></button>
            </div>
            <div style={styles.statsGrid}>
              {/* Status Card */}
              <div style={{ ...styles.statCard/* , gridColumn: '1 / -1'  */}}>
                <div style={styles.statTitle}>Status Breakdown</div>
                <SegmentBar segments={[
                  { value: pendingVal - advPendingVal, color: matte.blue, title: 'Pending', onClick: () => applyStatFilters({ statusGroup: 'PENDING' }) },
                  { value: advPendingVal, color: matte.amber, title: 'Adv Paid', onClick: () => applyStatFilters({ statusGroup: 'PENDING', advancePaidPending: '1' }) },
                  { value: convertedVal, color: matte.green, title: 'Converted', onClick: () => applyStatFilters({ status: 'CONVERTED' }) },
                  { value: rejectedVal, color: matte.red, title: 'Rejected', onClick: () => applyStatFilters({ status: 'REJECTED' }) },
                ]} />
                <div style={styles.legendGrid}>
                  <LegendItem label="Pending" value={pendingVal - advPendingVal} amount={pendingNoAdvAmt} color={matte.blue} active={isStatActive('PENDING')} onClick={() => applyStatFilters({ statusGroup: 'PENDING' })} />
                  <LegendItem label="Adv Paid" value={advPendingVal} amount={advPendingAmt} color={matte.amber} active={isStatActive('ADVANCE_PAID_PENDING')} onClick={() => applyStatFilters({ statusGroup: 'PENDING', advancePaidPending: '1' })} />
                  <LegendItem label="Converted" value={convertedVal} amount={convAmt} color={matte.green} active={isStatActive('CONVERTED')} onClick={() => applyStatFilters({ status: 'CONVERTED' })} />
                  <LegendItem label="Rejected" value={rejectedVal} amount={rejAmt} color={matte.red} active={isStatActive('REJECTED')} onClick={() => applyStatFilters({ status: 'REJECTED' })} />
                  <LegendItem label="All" value={stats.totalCount} amount={totalAmt} color={matte.grey} active={isStatActive('ALL')} onClick={() => applyStatFilters({})} />
          </div>
          </div>
              {/* Source Card */}
              <div style={styles.statCard}>
                <div style={styles.statTitle}>Source Breakdown</div>
                <SegmentBar segments={[
                  { value: directVal, color: matte.blue, title: 'Direct', onClick: () => applyStatFilters({ source: 'DIRECT' }) },
                  { value: orderVal, color: matte.rose, title: 'Order', onClick: () => applyStatFilters({ source: 'ORDER' }) },
                  { value: recurringVal, color: matte.teal, title: 'Recurring', onClick: () => applyStatFilters({ source: 'RECURRING' }) },
                ]} />
                <div style={styles.legendGrid}>
                  <LegendItem label="Direct" value={directVal} amount={directSrcAmt} color={matte.blue} active={isStatActive('SRC_DIRECT')} onClick={() => applyStatFilters({ source: 'DIRECT' })} />
                  <LegendItem label="Order" value={orderVal} amount={orderSrcAmt} color={matte.rose} active={isStatActive('SRC_ORDER')} onClick={() => applyStatFilters({ source: 'ORDER' })} />
                  <LegendItem label="Recurring" value={recurringVal} amount={recurringSrcAmt} color={matte.teal} active={isStatActive('SRC_RECURRING')} onClick={() => applyStatFilters({ source: 'RECURRING' })} />
                  <LegendItem label="All" value={stats.totalCount} amount={totalAmt} color={matte.grey} active={isStatActive('ALL')} onClick={() => applyStatFilters({})} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {!showStats && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button style={styles.btnSecondary} onClick={() => setShowStats(true)}><FiChevronDown size={14} /> Show Stats</button>
        </div>
      )}

      {/* Filters */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
            <div style={styles.searchWrapper}>
              <FiSearch size={14} style={styles.searchIcon} />
              <input style={styles.searchInput} placeholder="Quote number..." value={filters.number} onChange={e => setFilters(f => ({ ...f, number: e.target.value }))} />
            </div>
            <select style={styles.select} value={filters.customerId} onChange={e => setFilters(f => ({ ...f, customerId: e.target.value }))}>
              <option value="">All Customers</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button style={styles.btnSecondary} onClick={() => { setFilters(f => ({ ...f, number: '', customerId: '', status: '', statusGroup: '', source: '', advancePaidPending: '' })); setPage(1); }}>Clear</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={styles.card}>
        <div style={styles.tableWrap}>
          <table className="billing-table" style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 32 }}></th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Number</th>
                <th style={styles.th}>Source</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Advances</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
              {loading && (
                <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center' }}>
                  <div style={{ display: 'inline-block', width: 20, height: 20, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </td></tr>
              )}
              {!loading && !quotes.length && (
                <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: colors.stone400, fontSize: 12 }}>No quotes found</td></tr>
              )}
              {!loading && quotes.map(q => (
                <tr key={q.id} className="billing-row" style={{ transition: 'background 0.15s' }}>
                  <td style={styles.td}><input type="checkbox" checked={selectedIds.includes(q.id)} onChange={() => toggleSelected(q.id)} style={{ accentColor: colors.accent600 }} /></td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12, color: colors.stone500 }}>{q.date}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}><button style={styles.linkBtn} onClick={() => openPdfPreview(q)}>{q.number}</button></td>
                  <td style={{ ...styles.td, fontSize: 12 }}><span style={{ ...styles.badge, ...getSourceBadge(q.source) }}>{getSourceBadge(q.source).label}</span></td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    {q.customerSnapshot?.id || q.customerId ? (
                      <button style={styles.linkBtn} onClick={() => openCustomerDetail(q)}>{q.customerSnapshot?.name || 'Customer'}</button>
                  ) : (q.customerSnapshot?.name || '-')}
                </td>
                  <td style={styles.td}>
                    {q.status === 'CONVERTED' ? (
                      <button style={{ ...styles.linkBtn, color: colors.emerald600, fontWeight: 600 }} onClick={() => openInvoicePdfPreview(q)}>{q.convertedInvoiceNumber || 'Invoice'}</button>
                    ) : (
                      <span style={styles.statusWithDot}>
                        <span style={styles.statusDot(getStatusDotColor(q.status))} />
                        {q.status || '-'}
                      </span>
                    )}
                </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(q.proformas || []).map(p => {
                      const isPaid = String(p?.paymentStatus || '').toUpperCase() === 'PAID';
                        const amt = isPaid ? Number(p.amountPaid || 0) : Number(p.total || 0);
                      return (
                          <button key={p.id} style={{ ...styles.btnSecondary, fontSize: 10, padding: '2px 6px', borderRadius: 10, ...(isPaid ? {} : { borderColor: colors.rose600, color: colors.rose700 }) }}
                          onClick={async () => {
                              if (!isPaid) { setProformaPayModal({ open: true, proformaId: p.id, paymentMethod: '', paymentReference: '', paymentDate: new Date().toISOString().slice(0,10), proofUrl: '', uploading: false }); return; }
                              try {
                                setPdfPreview({ open: true, url: '', label: p.number || formatINR(amt), kind: 'Proforma', loading: true });
                              const { data } = await api.get(`/api/proformas/${p.id}/pdf-url`);
                                if (!data?.url) throw new Error('No URL');
                                setPdfPreview(pv => ({ ...pv, url: data.url, loading: false }));
                              } catch { setToast('Failed to load Proforma PDF'); setPdfPreview({ open: false, url: '', label: '', kind: 'Proforma', loading: false }); }
                            }}
                            title={isPaid ? 'Paid' : 'Unpaid - click to record payment'}
                          >{formatINR(amt)}</button>
                      );
                    })}
                      {(!q.proformas || !q.proformas.length) && <span style={{ color: colors.stone400 }}>-</span>}
                  </div>
                </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: colors.stone800, fontFeatureSettings: '"tnum"' }}>
                    {(() => {
                      const paidAdvance = (q.proformas || []).reduce((acc, p) => {
                        const isPaid = String(p?.paymentStatus || '').toUpperCase() === 'PAID';
                        return acc + (isPaid ? Number(p?.amountPaid || 0) : 0);
                      }, 0);
                      const pending = Math.max(0, +(Number(q.total || 0) - paidAdvance).toFixed(2));
                      const showPending = q.status !== 'CONVERTED' && paidAdvance > 0 && pending > 0;

                      const totalNode = q.status === 'CONVERTED' ? (
                        <button style={{ ...styles.linkBtn, fontWeight: 600, color: colors.stone800 }} onClick={async () => {
                          try {
                            const { data } = await api.get(`/api/quotes/${q.id}/invoice-pdf-url`);
                            if (!data?.invoiceId) { setToast('Invoice not found'); return; }
                            const { data: inv } = await api.get(`/api/invoices/${data.invoiceId}`);
                            setPayInfo({ open: true, inv: inv?.invoice || inv });
                          } catch { setToast('Failed to load payment details'); }
                        }}>{formatINR(q.total)}</button>
                      ) : (
                        <span style={{ fontWeight: 600, color: colors.stone800 }}>{formatINR(q.total)}</span>
                      );

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.15 }}>
                          <div>{totalNode}</div>
                          {showPending && (
                            <div style={{ fontSize: 11, color: colors.stone500, marginTop: 3 }}>
                              Pending: {formatINR(pending)}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      {q.status !== 'CONVERTED' && q.status !== 'REJECTED' && !(q.proformas || []).some(p => String(p?.paymentStatus || '').toUpperCase() === 'PAID') && (
                        <button style={styles.btnIcon} onClick={() => handleReject(q.id)} disabled={!!actionLoading[`reject:${q.id}`]} title="Reject"><FiXCircle size={14} color={colors.rose600} /></button>
                    )}
                    {q.status !== 'CONVERTED' && q.status !== 'REJECTED' && q.status !== 'CANCELLED' && (
                        <>
                          <button style={styles.btnIcon} onClick={() => openPayModal(q)} title="Accept & Pay"><FiCheckCircle size={14} color={colors.emerald600} /></button>
                          <button style={{ ...styles.btnSecondary, fontSize: 10, padding: '3px 6px' }} onClick={() => openAdvanceModal(q)}><FiPlus size={12} /> Adv</button>
                          <Link to={`/quote/${q.id}/edit`} style={styles.btnIcon}><FiEdit2 size={14} /></Link>
                        </>
                      )}
                      <Link to={`/quote/new?from=${q.id}`} style={styles.btnIcon} title="Duplicate"><FiCopy size={14} /></Link>
                      <button style={styles.btnIcon} onClick={() => handleSendEmail(q.id)} disabled={!!actionLoading[`email:${q.id}`]} title="Email"><FiMail size={14} /></button>
                      <button style={styles.btnIcon} onClick={() => openWhatsAppModal(q)} title="WhatsApp"><FiMessageCircle size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
                </div>
        {renderPagination()}
                </div>

      {/* PDF Preview Drawer */}
        {pdfPreview.open && (
        <div style={styles.drawer} onClick={closePdfPreview}>
          <div style={styles.drawerContent} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>{pdfPreview.kind} - {pdfPreview.label}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {!!pdfPreview.url && <a href={pdfPreview.url} target="_blank" rel="noreferrer" style={{ ...styles.btnSecondary, textDecoration: 'none' }}><FiExternalLink size={14} /> Open</a>}
                <button style={styles.btnSecondary} onClick={closePdfPreview}>Close</button>
                </div>
              </div>
            <div style={{ flex: 1, background: colors.stone100 }}>
                {pdfPreview.loading ? (
                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 24, height: 24, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  </div>
              ) : pdfPreview.url ? (
                <iframe title="PDF" src={pdfPreview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : <div style={{ padding: 20, color: colors.stone500 }}>No preview</div>}
              </div>
            </div>
          </div>
        )}

      {/* Customer Drawer */}
        {customerDrawer.open && (
        <div style={styles.drawer} onClick={() => setCustomerDrawer({ open: false, id: null, customer: null })}>
          <div style={{ ...styles.drawerContent, width: '70%', maxWidth: 1000 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Customer</h3>
              <button style={styles.btnSecondary} onClick={() => setCustomerDrawer({ open: false, id: null, customer: null })}>Close</button>
                </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
                  <CustomerDetail embeddedCustomerId={customerDrawer.id} embeddedCustomer={customerDrawer.customer} />
              </div>
            </div>
          </div>
        )}

      {/* Payment Info Modal */}
      {payInfo.open && payInfo.inv && (
        <div style={styles.modal} onClick={() => setPayInfo({ open: false, inv: null })}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}><h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Payment Details</h3><button style={styles.btnIcon} onClick={() => setPayInfo({ open: false, inv: null })}><FiX size={18} /></button></div>
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><div style={styles.label}>Method</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.inv.paymentMethod || '-'}</div></div>
                <div><div style={styles.label}>Reference</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.inv.paymentReference || '-'}</div></div>
                <div><div style={styles.label}>Date</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.inv.paymentDate ? new Date(payInfo.inv.paymentDate).toISOString().slice(0,10) : '-'}</div></div>
                <div><div style={styles.label}>Amount</div><div style={{ fontSize: 13, color: colors.stone800 }}>{formatINR(payInfo.inv.amountPaid || payInfo.inv.total)}</div></div>
          </div>
              {payInfo.inv.paymentProofUrl && (
                <div style={{ marginTop: 12 }}><div style={styles.label}>Proof</div><a href={payInfo.inv.paymentProofUrl} target="_blank" rel="noreferrer"><img src={payInfo.inv.paymentProofUrl} alt="Proof" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: `1px solid ${colors.stone200}` }} /></a></div>
              )}
        </div>
            <div style={styles.modalFooter}><button style={styles.btnSecondary} onClick={() => setPayInfo({ open: false, inv: null })}>Close</button></div>
      </div>
        </div>
      )}

      {/* Bulk Convert Modal */}
      {bulkConvert.open && (
        <div style={styles.modal} onClick={() => setBulkConvert({ ...bulkConvert, open: false })}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}><h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Consolidate to Invoice</h3><button style={styles.btnIcon} onClick={() => setBulkConvert({ ...bulkConvert, open: false })}><FiX size={18} /></button></div>
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={styles.formGroup}><label style={styles.label}>Due Date</label><input style={styles.input} type="date" value={bulkConvert.dueDate} onChange={e => setBulkConvert({ ...bulkConvert, dueDate: e.target.value })} /></div>
                <div style={styles.formGroup}><label style={styles.label}>Payment Date</label><input style={styles.input} type="date" value={bulkConvert.paymentDate} onChange={e => setBulkConvert({ ...bulkConvert, paymentDate: e.target.value })} /></div>
                <div style={styles.formGroup}><label style={styles.label}>Payment Method</label><select style={styles.input} value={bulkConvert.paymentMethod} onChange={e => setBulkConvert({ ...bulkConvert, paymentMethod: e.target.value })}><option value="">None</option><option value="CASH">Cash</option><option value="UPI">UPI</option><option value="CARD">Card</option><option value="BANK_TRANSFER">Bank Transfer</option><option value="CHEQUE">Cheque</option></select></div>
                <div style={styles.formGroup}><label style={styles.label}>Reference</label><input style={styles.input} value={bulkConvert.paymentReference} onChange={e => setBulkConvert({ ...bulkConvert, paymentReference: e.target.value })} /></div>
              </div>
              </div>
            <div style={styles.modalFooter}><button style={styles.btnSecondary} onClick={() => setBulkConvert({ ...bulkConvert, open: false })}>Cancel</button><button style={styles.btnSecondary} onClick={confirmBulkConvert}>Create Invoice</button></div>
              </div>
              </div>
      )}

      {/* Accept & Pay Modal */}
      <PaymentModal
        isOpen={payModal.open}
        onClose={() => setPayModal({ ...payModal, open: false })}
        onSubmit={handleSubmitAcceptAndPay}
        title="Accept Quote"
        submitText="Accept & Mark Paid"
        submitLoadingText="Processing..."
        isSubmitting={!!actionLoading[`accept:${payModal.id}`]}
        acceptPdfProof={true}
      />

      {/* Advance Modal */}
      {advanceModal.open && (
        <div style={styles.modal} onClick={() => setAdvanceModal({ ...advanceModal, open: false })}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}><h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Record Advance</h3><button style={styles.btnIcon} onClick={() => setAdvanceModal({ ...advanceModal, open: false })}><FiX size={18} /></button></div>
            <div style={styles.modalBody}>
            {advanceModal.summary && (
                <div style={{ background: colors.stone50, border: `1px solid ${colors.stone200}`, borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 12 }}>
                  <div>Received: <strong>{formatINR(advanceModal.summary.received)}</strong></div>
                  <div>Remaining: <strong>{formatINR(advanceModal.summary.remaining)}</strong></div>
                  <div>Pending advances: <strong>{formatINR(pendingAdvanceAmount)}</strong></div>
              </div>
            )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={styles.formGroup}><label style={styles.label}>Amount (₹)</label><input style={styles.input} type="number" value={advanceModal.amount} onChange={e => setAdvanceModal({ ...advanceModal, amount: e.target.value })} /></div>
                <div style={styles.formGroup}><label style={styles.label}>Or Percentage (%)</label><input style={styles.input} type="number" value={advanceModal.percentage} onChange={e => setAdvanceModal({ ...advanceModal, percentage: e.target.value })} /></div>
                <div style={styles.formGroup}><label style={styles.label}>Payment Method</label><select style={styles.input} value={advanceModal.paymentMethod} onChange={e => setAdvanceModal({ ...advanceModal, paymentMethod: e.target.value })}><option value="">Select</option><option value="CASH">Cash</option><option value="UPI">UPI</option><option value="CARD">Card</option><option value="BANK_TRANSFER">Bank Transfer</option><option value="CHEQUE">Cheque</option></select></div>
                <div style={styles.formGroup}><label style={styles.label}>Payment Date</label><input style={styles.input} type="date" value={advanceModal.paymentDate} onChange={e => setAdvanceModal({ ...advanceModal, paymentDate: e.target.value })} /></div>
              </div>
              <div style={styles.formGroup}><label style={styles.label}>Reference</label><input style={styles.input} value={advanceModal.paymentReference} onChange={e => setAdvanceModal({ ...advanceModal, paymentReference: e.target.value })} /></div>
              <div style={styles.formGroup}><label style={styles.label}>Notes</label><textarea style={{ ...styles.input, minHeight: 60 }} value={advanceModal.notes} onChange={e => setAdvanceModal({ ...advanceModal, notes: e.target.value })} /></div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, color: colors.stone600 }}><input type="checkbox" checked={advanceModal.paidNow} onChange={e => setAdvanceModal({ ...advanceModal, paidNow: e.target.checked })} /> Mark as paid now</label>
              </div>
            <div style={styles.modalFooter}><button style={styles.btnSecondary} onClick={() => setAdvanceModal({ ...advanceModal, open: false })}>Cancel</button><button style={styles.btnSecondary} onClick={submitAdvance} disabled={advanceModal.loading}>{advanceModal.loading ? 'Saving...' : 'Record Advance'}</button></div>
              </div>
              </div>
      )}

      {/* Proforma Payment Modal */}
      <PaymentModal
        isOpen={proformaPayModal.open}
        onClose={() => setProformaPayModal({ ...proformaPayModal, open: false })}
        onSubmit={submitProformaPayment}
        title="Record Proforma Payment"
        submitText="Mark Paid"
        submitLoadingText="Processing..."
        isSubmitting={!!actionLoading[`proformaPay:${proformaPayModal.proformaId}`]}
        acceptPdfProof={true}
      />

      {/* WhatsApp Modal */}
      {waModal.open && (
        <div style={styles.modal} onClick={() => setWaModal({ open: false, id: null, phone: '', message: '' })}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}><h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Send via WhatsApp</h3><button style={styles.btnIcon} onClick={() => setWaModal({ open: false, id: null, phone: '', message: '' })}><FiX size={18} /></button></div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}><label style={styles.label}>Phone</label><input style={styles.input} value={waModal.phone} onChange={e => setWaModal({ ...waModal, phone: e.target.value })} /></div>
              <div style={styles.formGroup}><label style={styles.label}>Message</label><textarea style={{ ...styles.input, minHeight: 80 }} value={waModal.message} onChange={e => setWaModal({ ...waModal, message: e.target.value })} /></div>
            </div>
            <div style={styles.modalFooter}><button style={styles.btnSecondary} onClick={() => setWaModal({ open: false, id: null, phone: '', message: '' })}>Cancel</button><button style={styles.btnSecondary} onClick={handleSendWhatsApp}>Open WhatsApp</button></div>
          </div>
        </div>
      )}

      {/* Number Format Modal */}
      {formatModal && (
        <div style={styles.modal} onClick={() => setFormatModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}><h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Quote Number Format</h3><button style={styles.btnIcon} onClick={() => setFormatModal(false)}><FiX size={18} /></button></div>
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr', gap: 12 }}>
                <div style={styles.formGroup}><label style={styles.label}>Prefix</label><input style={styles.input} value={seriesState.prefixPre} onChange={e => setSeriesState(s => ({ ...s, prefixPre: e.target.value.replace(seriesState.fy, '').replace(new RegExp(`${seriesState.fySep}$`), '') }))} placeholder="QTN" /></div>
                <div style={styles.formGroup}><label style={styles.label}>FY</label><input style={{ ...styles.input, background: colors.stone100, color: colors.stone500 }} value={seriesState.fy ? `${seriesState.fySep}${seriesState.fy}${seriesState.fySep}` : '-'} readOnly /></div>
                <div style={styles.formGroup}><label style={styles.label}>Suffix</label><input style={styles.input} value={seriesState.suffix} onChange={e => setSeriesState(s => ({ ...s, suffix: e.target.value }))} placeholder="-A" /></div>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: colors.stone500 }}>
                Example: <span style={{ fontFamily: 'monospace', color: colors.stone700 }}>{seriesState.prefixPre}{seriesState.fySep}{seriesState.fy}{seriesState.fySep}{String((seriesState.currentSeq || 0) + 1).padStart(6, '0')}{seriesState.suffix}</span>
            </div>
            {!!formatOptions.length && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.stone700, marginBottom: 8 }}>Existing Formats</div>
                  <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {formatOptions.map(r => {
                      const fy = r.fyStart && r.fyEnd ? `${String(new Date(r.fyStart).getUTCFullYear()).slice(-2)}-${String(new Date(r.fyEnd).getUTCFullYear()).slice(-2)}` : '';
                    return (
                        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: colors.stone50, border: `1px solid ${colors.stone200}`, borderRadius: 8, fontSize: 11 }}>
                          <div><span style={{ fontFamily: 'monospace' }}>{r.prefix}</span>{r.suffix && <span> <b>Suffix:</b> {r.suffix}</span>}{r.isActive && <span style={{ marginLeft: 8, padding: '2px 6px', background: colors.emerald50, color: colors.emerald700, borderRadius: 4, fontSize: 10 }}>Active</span>}</div>
                          <button style={styles.btnSecondary} onClick={() => applyExistingFormat(r)} disabled={r.isActive || formatActivatingId === r.id}>{r.isActive ? 'In use' : formatActivatingId === r.id ? '...' : 'Use'}</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </div>
            <div style={styles.modalFooter}><button style={styles.btnSecondary} onClick={() => setFormatModal(false)}>Cancel</button><button style={styles.btnSecondary} onClick={saveSeriesFormat} disabled={seriesState.saving}>{seriesState.saving ? 'Saving...' : 'Save'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
